import * as MessageActions from "./message-actions";
import { ChatReducerHandler } from "./types";

export const queryMessagesPending: ChatReducerHandler<MessageActions.IQueryMessagesPendingAction> =
    (state, action) => ({
        ...state,
        messages: {
            ...state.messages,
            isLoading: {
                ...state.messages.isLoading,
                [action.meta.conversationId]: true,
            },
        },
    });

export const queryMessagesRejected: ChatReducerHandler<MessageActions.IQueryMessagesRejectedAction> =
    (state, action) => ({
        ...state,
        messages: {
            ...state.messages,
            isLoading: {
                ...state.messages.isLoading,
                [action.meta.conversationId]: false,
            },
        },
    });

export const queryMessagesFulFilled: ChatReducerHandler<MessageActions.IQueryMessagesFulFilledAction> =
    (state, action) => {
        const newMessages = action.payload.data
            .filter(m => state.messages.items.every(msg => msg.id !== m.id));

        return {
            ...state,
            messages: {
                ...state.messages,
                items: [
                    ...newMessages.map((x) => ({...x, date: new Date(x.date)})),
                    ...state.messages.items
                ].sort((a, b) => a.date < b.date ? -1 : 1),
                isLoading: {
                    ...state.messages.isLoading,
                    [action.meta.conversationId]: false,
                },
                hasMore: {
                    ...state.messages.hasMore,
                    [action.meta.conversationId]: action.payload.data.length === action.meta.take,
                },
                virtuosoIndex: {
                    ...state.messages.virtuosoIndex,
                    [action.meta.conversationId]: (state.messages.virtuosoIndex[action.meta.conversationId] ?? 100000) - newMessages.length,
                },
            }
        };
    };

export const queryPinnedMessagesFulfilled: ChatReducerHandler<MessageActions.IQueryPinnedMessagesFulfilledAction> =
    (state, action) => ({
        ...state,
        conversations: {
            ...state.conversations,
            pinnedMessages: {
                ...state.conversations.pinnedMessages,
                [action.meta.conversationId]: action.payload.data,
            },
        },
    });

export const addMessageFulfilled: ChatReducerHandler<MessageActions.IMessageReceivedAction> =
    (state, action) => {
        if (action.meta.message.isSystem) {
            return state;
        }

        if (state.messages.items.some((m) => m.id === action.meta.message.id) ||
            (action.meta.message.referenceId && state.messages.items.some((m) => m.referenceId === action.meta.message.referenceId))
        ) {
            return {
                ...state,
                messages: {
                    ...state.messages,
                    items: state.messages.items.map((m) => {
                        if (m.id === action.meta.message.id) {
                            return {
                                ...m,
                                ...action.meta.message,
                                date: m.date
                            };
                        }

                        if (!!m.parent && m.parent.id === action.meta.message.id) {
                            return {
                                ...m,
                                parent: {
                                    ...m.parent,
                                    ...action.meta.message,
                                    date: m.parent.date
                                }
                            }
                        }

                        return m;
                    }),
                },
            };
        }

        return {
            ...state,
            messages: {
                ...state.messages,
                items: [
                    ...state.messages.items,
                    {
                        ...action.meta.message,
                        date: new Date(action.meta.message.date)
                    },
                ],
            },
        };
    };

export const removeMessageFulfilled: ChatReducerHandler<MessageActions.IMessageDeletedAction> =
    (state, action) => ({
        ...state,
        messages: {
            ...state.messages,
            items: state.messages.items.map((m) => {
                if (m.id === action.meta.messageId) {
                    return {
                        ...m,
                        isDeleted: true,
                        isPinned: false,
                        isStarred: false,
                        isMemo: false,
                        memoTitle: "",
                        text: "",
                        attachments: [],
                        reactions: [],
                        object: undefined,
                        objects: [],
                    };
                }

                if (!!m.parent && m.parent.id === action.meta.messageId) {
                    return {
                        ...m,
                        parent: {
                            ...m.parent,
                            isDeleted: true,
                            isPinned: false,
                            isStarred: false,
                            isMemo: false,
                            memoTitle: "",
                            text: "",
                            attachments: [],
                            reactions: [],
                            object: undefined,
                            objects: [],
                        }
                    };
                }

                return m;
            }),
        },
        conversations: {
            ...state.conversations,
            items: state.conversations.items.map((c) => {
                if (!c.lastMessage || c.lastMessage.id !== action.meta.messageId) {
                    return c;
                }

                return {
                    ...c,
                    lastMessage: {
                        ...c.lastMessage,
                        isDeleted: true,
                        isMemo: false,
                        memoTitle: "",
                        text: "",
                    },
                };
            }),
            pinnedMessages: {
                ...state.conversations.pinnedMessages,
                ...Object.keys(state.conversations.pinnedMessages || {}).reduce((acc, key) => {
                    acc[key] = (state.conversations.pinnedMessages[key] || []).filter((x) => x.id !== action.meta.messageId);
                    return acc;
                }, {} as { [key: string]: Spintr.IChatMessage[] }),
            },
        },
    });

export const pinMessageFulfilled: ChatReducerHandler<MessageActions.IMessagePinnedAction> =
    (state, action) => ({
        ...state,
        messages: {
            ...state.messages,
            items: state.messages.items.map(m => {
                if (m.id !== action.meta.message.id) {
                    return m;
                }

                return {
                    ...m,
                    isPinned: true,
                };
            }),
        },
        conversations: {
            ...state.conversations,
            pinnedMessages: {
                ...state.conversations.pinnedMessages,
                [action.meta.message.conversationId]: [
                    ...(state.conversations.pinnedMessages[action.meta.message.conversationId] || [])
                        .filter(x => x.id !== action.meta.message.id),
                    action.meta.message,
                ],
            },
        },
    });

export const unpinMessageFulfilled: ChatReducerHandler<MessageActions.IMessageUnpinnedAction> =
    (state, action) => ({
        ...state,
        messages: {
            ...state.messages,
            items: state.messages.items.map(m => {
                if (m.id !== action.meta.message.id) {
                    return m;
                }

                return {
                    ...m,
                    isPinned: false,
                };
            })
        },
        conversations: {
            ...state.conversations,
            pinnedMessages: {
                ...state.conversations.pinnedMessages,
                [action.meta.message.conversationId]: (state.conversations.pinnedMessages[action.meta.message.conversationId] || [])
                    .filter(x => x.id !== action.meta.message.id),
            },
        },
    });

export const removeReaction: ChatReducerHandler<MessageActions.RemoveReactionAction> =
    (state, action) => {
        const message = state.messages.items.find(x => x.id === action.meta.messageId);

        if (!message || !message.reactions.some(x => x.user.id === action.meta.userId && x.likeType === action.meta.likeType)) {
            return state;
        }

        return {
            ...state,
            messages: {
                ...state.messages,
                items: state.messages.items.map(m => {
                    if (m.id !== action.meta.messageId) {
                        return m;
                    }

                    const existingReaction = m.reactions.find(r =>
                        r.likeType === action.meta.likeType &&
                        r.user.id === action.meta.userId);

                    if (!existingReaction) {
                        return m;
                    }

                    return {
                        ...m,
                        reactions: [
                            ...m.reactions.slice(0, m.reactions.indexOf(existingReaction)),
                            ...m.reactions.slice(m.reactions.indexOf(existingReaction) + 1)
                        ]
                    };
                }),
            },
        };
    };

export const addReaction: ChatReducerHandler<MessageActions.AddReactionAction> =
    (state, action) => {
        const message = state.messages.items.find(x => x.id === action.meta.messageId);

        if (!message || message.reactions.some(x => x.user.id === action.meta.userId && x.likeType === action.meta.likeType)) {
            return state;
        }

        return {
            ...state,
            messages: {
                ...state.messages,
                items: state.messages.items.map(m => {
                    if (m.id !== action.meta.messageId) {
                        return m;
                    }

                    const existingReaction = m.reactions.find(r =>
                        r.likeType === action.meta.likeType &&
                        r.user.id === action.meta.userId);

                    if (existingReaction) {
                        return m;
                    }

                    return {
                        ...m,
                        reactions: [
                            ...m.reactions.filter(x => x.user.id !== action.meta.userId),
                            {
                                likeType: action.meta.likeType,
                                user: {
                                    id: action.meta.userId,
                                    name: action.meta.userName
                                }
                            }
                        ]
                    };
                }),
            },
        };
    };

export const saveMessagePending: ChatReducerHandler<MessageActions.ISaveMessagePendingAction> =
    (state, action) => {
        if (state.messages.items.some(x => x.id === action.meta.message.id)) {
            return state;
        }

        return {
            ...state,
            messages: {
                ...state.messages,
                items: [ 
                    ...state.messages.items,
                    {
                        ...action.meta.message,
                        date: new Date(action.meta.message.date)
                    },
                ],
            },
        };
    };

export const saveMessageFulfilled: ChatReducerHandler<MessageActions.ISaveMessageFulfilledAction> = (state, action) => ({
    ...state,
    messages: {
        ...state.messages,
        items: state.messages.items.map((m) => {
            if (m.id !== action.meta.message.id) {
                return m;
            }

            if (!!action.payload.data.user &&
                !action.payload.data.user.imageUrl &&
                !!m.user &&
                !!m.user.imageUrl) {
                action.payload.data.user.imageUrl = m.user.imageUrl;
            }

            return {
                ...action.payload.data,
                referenceId: m.referenceId,
                date: new Date(action.payload.data.date)
            };
        })
    },
});

export const saveMessageRejected: ChatReducerHandler<MessageActions.ISaveMessageRejectedAction> =
    (state, action) => ({
        ...state,
        messages: {
            ...state.messages,
            items: state.messages.items.map((m) => {
                if (m.id !== action.meta.message.id) {
                    return m;
                }

                return {
                    ...m,
                    savingFailed: true,
                };
            }),
        },
    });

export const memoTodoUpdated: ChatReducerHandler<MessageActions.IMemoTodoUpdatedAction> =
    (state, action) => ({
        ...state,
        messages: {
            ...state.messages,
            items: state.messages.items.map((m) => {
                if ((m.objects || []).find(x => x.type === 28 && x.todos.find(t => t.id === action.meta.id))) {
                    return {
                        ...m,
                        objects: m.objects.map((o) => {
                            if (o.type === 28) {
                                return {
                                    ...o,
                                    todos: o.todos.map((t) => {
                                        if (t.id === action.meta.id) {
                                            return action.meta;
                                        }

                                        return t;
                                    })
                                }
                            }

                            return o;
                        })
                    }
                }

                return m;
            }),
        },
    });

export const updateMessageFullfilled: ChatReducerHandler<MessageActions.IUpdateMessageFulfilledAction> =
    (state, action) => ({
        ...state,
        messages: {
            ...state.messages,
            items: state.messages.items.map((m) => {
                if (m.id === action.meta.message.id) {
                    return {
                        ...action.meta.message,
                        date: m.date
                    };
                }

                if (!!m.parent && m.parent.id === action.meta.message.id) {
                    return {
                        ...m,
                        parent: {
                            ...action.meta.message,
                            date: m.parent.date
                        }
                    }
                }

                return m;
            }),
        },
    });
